<template>
  <v-tooltip v-if="showConnectivity" location="bottom" content-class="connection-status-tooltip">
    <template v-slot:activator="{ props }">
      <span :data-cy="`${connectionType}ConnectionStatus`">
        <v-btn v-bind="props" class="popover-status-icon" variant="text">
          <v-icon
            v-if="isDeviceConnected && connectionType === ConnectionType.WIFI"
            color="grey"
            :icon="connection.icon"
          />
          <img
            v-else-if="isDeviceConnected && connectionType === ConnectionType.ETHERNET"
            :src="ethernetIcon"
            alt="Ethernet Icon"
          />
          <v-icon v-else color="grey" :icon="connectionStates.disconnected.icon" />
        </v-btn>
      </span>
    </template>
    <div v-if="isDeviceConnected">
      {{ connectionText }}
    </div>
    <div v-else>
      {{ connectionStates.disconnected.label }}
    </div>
  </v-tooltip>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { ConnectionType } from '@/store/devices/utils'
import type { GetterByDeviceId, GetterByModel } from '@/components/devicePopover/types'

interface ConnectionInfo {
  label: string
  icon?: string
}

interface ConnectionStates {
  disconnected: {
    label: string
    icon: string
  }
}

interface EthernetConnectionMap {
  wired: ConnectionInfo
}

interface WifiConnectionMap {
  veryGood: ConnectionInfo
  good: ConnectionInfo
  okay: ConnectionInfo
  bad: ConnectionInfo
  unusable: ConnectionInfo
  na: ConnectionInfo
}

export default defineComponent({
  name: 'ConnectionStatus',
  props: {
    deviceId: { type: String }
  },
  data() {
    return {
      connectionStates: {
        disconnected: {
          label: this.$t('map.popover.timestamp.disconnected'),
          icon: 'mdi-wifi-strength-alert-outline'
        }
      } as ConnectionStates,
      ConnectionType,
      ethernetConnections: {
        wired: {
          label: this.$t('map.popover.connectionStrength.ethernet.wired')
        }
      } as EthernetConnectionMap,
      wifiConnections: {
        veryGood: {
          label: this.$t('map.popover.connectionStrength.wifi.veryGood'),
          icon: 'mdi-wifi-strength-4'
        },
        good: {
          label: this.$t('map.popover.connectionStrength.wifi.good'),
          icon: 'mdi-wifi-strength-3'
        },
        okay: {
          label: this.$t('map.popover.connectionStrength.wifi.okay'),
          icon: 'mdi-wifi-strength-2'
        },
        bad: {
          label: this.$t('map.popover.connectionStrength.wifi.bad'),
          icon: 'mdi-wifi-strength-2'
        },
        unusable: {
          label: this.$t('map.popover.connectionStrength.wifi.unusable'),
          icon: 'mdi-wifi-strength-1'
        },
        na: {
          label: this.$t('map.popover.valueBlank'),
          icon: 'mdi-wifi-strength-alert-outline'
        }
      } as WifiConnectionMap,
      ethernetIcon: '/img/icons/ethernet_icon_grey.svg'
    }
  },
  computed: {
    id(): string {
      return this.deviceId || ''
    },
    rssi(): number | null {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID}`
      ] as GetterByDeviceId<number | null>
      const value = getter(this.id)
      return typeof value === 'number' ? value : null
    },
    model(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.id) || ''
    },
    connectionType(): ConnectionType {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID}`
      ] as GetterByDeviceId<ConnectionType>
      return getter(this.id) || ConnectionType.WIFI
    },
    connection(): ConnectionInfo {
      return this.connectionType === ConnectionType.WIFI
        ? this.wifiConnection
        : this.ethernetConnections.wired
    },
    connectionText(): string {
      return this.connectionType === ConnectionType.WIFI
        ? `${this.$t('map.popover.timestamp.wifiStrength')}: ${this.connection.label}`
        : this.connection.label
    },
    wifiConnection(): ConnectionInfo {
      if (typeof this.rssi === 'number') {
        if (this.rssi > -67) return this.wifiConnections.veryGood
        if (this.rssi > -70) return this.wifiConnections.good
        if (this.rssi > -80) return this.wifiConnections.okay
        if (this.rssi > -90) return this.wifiConnections.bad
        return this.wifiConnections.unusable
      }

      return this.wifiConnections.na
    },
    isDeviceConnected(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
      ] as GetterByDeviceId<boolean>
      return !!getter(this.id)
    },
    showConnectivity(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_CONNECTIVITY}`
      ] as GetterByModel<boolean>
      return !!getter(this.model)
    }
  }
})
</script>
